/**
 * The single left sidebar (Studio + Creator).
 *
 * The membership is the parent, so it owns the sidebar on every page: brand and
 * nav, nothing else. The name, description and counts live in the page's right
 * rail (the membership banner) — repeating them here just said it twice.
 *
 * When `onToggle` is supplied (Creator) the rail collapses to an icons-only
 * strip; the parent tracks the `collapsed` state so it can shift the page's left
 * padding in step. Without it (Studio) the sidebar is a fixed 64-wide column.
 *
 * Sides are logical (`start-0`, `border-e`, `-end-3`), so with dir="rtl" the whole
 * rail swings to the right edge — its border, its toggle and the page padding in
 * MembershipShell all follow. Pinned physically it stayed on the left while the
 * rest of the page mirrored, and the content ran underneath it.
 */
export default function MembershipSidebar({
  logo,
  logoIcon,
  tabs = [],
  activeKey,
  isActive,
  onNavigate,
  member,
  onJoin,
  primary = "#0274F7",
  collapsed = false,
  onToggle,
  // `belowHeader` (Creator): the logo stays in the full-width top bar, so the
  // sidebar sits UNDER the header (top-16) and only carries the menus.
  belowHeader = false,
  // "spotlight": full height, logo + collapse button sharing the top row, and
  // the club-style nav (quiet warm items, brand-tinted active pill). Everything
  // else about the rail is shared.
  variant = "default",
  // Extra entries after the section nav (Spotlight's "My Profile"):
  // [{ key, label, icon, onClick, active }].
  footerItems = [],
}) {
  const tabActive = (key) => (isActive ? isActive(key) : activeKey === key);

  if (variant === "spotlight") {
    return (
      <SpotlightRail
        logo={logo}
        logoIcon={logoIcon}
        tabs={tabs}
        tabActive={tabActive}
        onNavigate={onNavigate}
        primary={primary}
        collapsed={collapsed}
        onToggle={onToggle}
        footerItems={footerItems}
      />
    );
  }

  const toggleBtn = onToggle ? (
    <button
      type="button"
      onClick={onToggle}
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      className="absolute -end-3 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-surface text-gray-500 shadow-sm transition hover:text-gray-800 hover:shadow"
      style={{ top: belowHeader ? "1rem" : "50%", transform: belowHeader ? "none" : "translateY(-50%)" }}
    >
      <svg className={`h-4 w-4 transition-transform ${collapsed ? "rtl:rotate-180" : "rotate-180 rtl:rotate-0"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  ) : null;

  return (
    <aside
      className={`fixed start-0 z-40 hidden flex-col border-e border-gray-100 bg-surface transition-[width] duration-300 ease-out lg:flex ${collapsed ? "w-16" : "w-64"} ${belowHeader ? "top-16 h-[calc(100vh-4rem)]" : "top-0 h-screen"}`}
    >
      {belowHeader ? (
        // Logo lives in the top bar; a slim spacer holds the toggle.
        <div className="relative h-3 shrink-0">{toggleBtn}</div>
      ) : (
        <div className={`relative flex h-16 shrink-0 items-center border-b border-gray-50 ${collapsed ? "justify-center px-2" : "px-6"}`}>
          {collapsed ? (logoIcon || null) : logo}
          {toggleBtn}
        </div>
      )}

      <div className="thin-scroll flex-1 overflow-y-auto px-3 py-5">
        <nav className="space-y-1">
          {tabs.map((t) => {
            const active = tabActive(t.key);
            return (
              <button
                key={t.key}
                onClick={() => onNavigate?.(t.key)}
                title={collapsed ? t.label : undefined}
                className={`flex w-full items-center gap-3 rounded-xl py-2.5 text-sm font-semibold transition-colors ${collapsed ? "justify-center px-0" : "px-3"} ${active ? "" : "text-gray-600"}`}
                style={active ? { backgroundColor: `${primary}14`, color: primary } : undefined}
                // Theme-tinted hover, matching the inline/sub nav in the other
                // templates (classic/catalog/campus) instead of a grey wash.
                onMouseEnter={active ? undefined : (e) => { e.currentTarget.style.background = `${primary}0f`; e.currentTarget.style.color = primary; }}
                onMouseLeave={active ? undefined : (e) => { e.currentTarget.style.background = ""; e.currentTarget.style.color = ""; }}
              >
                <span className="shrink-0">{t.icon}</span>
                {!collapsed && <span className="truncate">{t.label}</span>}
              </button>
            );
          })}
        </nav>
      </div>

      {!member && onJoin && !collapsed && (
        <div className="border-t border-gray-50 px-4 py-4">
          <button
            onClick={onJoin}
            style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
            className="w-full rounded-xl py-2.5 font-bold text-white shadow-lg transition hover:opacity-90"
          >
            Join Community
          </button>
        </div>
      )}
    </aside>
  );
}

/*
 * Spotlight's rail. Kept apart from the default one rather than threaded
 * through it with conditionals: the two share a job, not a shape — Spotlight's
 * toggle sits beside the logo instead of floating on the edge, and its items
 * are the quieter club-style row.
 */
const PANEL_ICON = "M9 3.75v16.5M4.5 3.75h15a.75.75 0 0 1 .75.75v15a.75.75 0 0 1-.75.75h-15a.75.75 0 0 1-.75-.75v-15a.75.75 0 0 1 .75-.75Zm8.25 5.5L15.5 12l-2.75 2.75";

function SpotlightRail({ logo, logoIcon, tabs, tabActive, onNavigate, primary, collapsed, onToggle, footerItems }) {
  const item = (key, label, icon, active, onClick) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      title={collapsed ? label : undefined}
      aria-current={active ? "page" : undefined}
      className={`flex h-10 w-full items-center gap-3 rounded-[14px] text-[14px] font-medium transition-colors ${collapsed ? "justify-center px-0" : "px-3"} ${active ? "" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"}`}
      style={active ? { backgroundColor: `${primary}1f`, color: primary } : undefined}
    >
      <span className="shrink-0 [&_svg]:h-[18px] [&_svg]:w-[18px]">{icon}</span>
      {!collapsed && <span className="truncate">{label}</span>}
    </button>
  );

  const toggle = onToggle ? (
    <button
      type="button"
      onClick={onToggle}
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
    >
      <svg className={`h-[18px] w-[18px] transition-transform ${collapsed ? "rotate-180 rtl:rotate-0" : "rtl:rotate-180"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6">
        <path strokeLinecap="round" strokeLinejoin="round" d={PANEL_ICON} />
      </svg>
    </button>
  ) : null;

  return (
    <aside
      className={`fixed start-0 top-0 z-40 hidden h-screen flex-col border-e border-gray-200 bg-surface transition-[width] duration-300 ease-out lg:flex ${collapsed ? "w-[72px]" : "w-64"}`}
    >
      <div className={`flex shrink-0 border-b border-gray-200 ${collapsed ? "h-auto flex-col items-center gap-2 py-4" : "h-[72px] items-center justify-between gap-2 ps-6 pe-4"}`}>
        <div className="min-w-0">{collapsed ? (logoIcon || null) : logo}</div>
        {toggle}
      </div>
      <nav className="thin-scroll flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {tabs.map((t) => item(t.key, t.label, t.icon, tabActive(t.key), () => onNavigate?.(t.key)))}
        {footerItems.map((f) => item(f.key, f.label, f.icon, !!f.active, f.onClick))}
      </nav>
    </aside>
  );
}
